import { useCallback, useEffect, useMemo, useRef, useState, useSyncExternalStore } from 'react'
import type { IterableEmbeddedMessage } from '@iterable/web-sdk'
import { useInterval } from '@quickmd-llc/frontend-utils'
import { captureException } from '@sentry/react'
import { getAnnouncementPreview } from 'features/devTools/announcementOverrides'
import {
  getIsIterableIdentified,
  getIterableUserId,
  publishDismissedCampaigns,
  subscribeToIterableIdentity,
} from './client'
import { APP_PACKAGE_NAME, POLL_INTERVAL_MS } from './constants'
import { getDismissedCampaignIds, getDismissedMessageIds, recordDismissedMessageId } from './dismissedAnnouncements'
import { embeddedManager, subscribeToEmbeddedMessages, trackDismiss } from './embedded'
import { isRenderable, type RenderableMessage } from './types'

type UseIterableEmbeddedMessages = {
  /** Every live message for the placement, in the order Iterable returned them. */
  messages: RenderableMessage[]
  dismiss: (message: IterableEmbeddedMessage, buttonIdentifier: string) => void
}

/**
 * Whether Iterable knows who the provider is yet.
 *
 * Separate from the placement's own flag because it is not a decision, it is an arrival: identity
 * lands two network round trips after the dashboard mounts, and until it does every
 * embedded-messaging request is refused and discarded without a trace.
 */
export function useIsIterableIdentified(): boolean {
  return useSyncExternalStore(subscribeToIterableIdentity, getIsIterableIdentified, () => false)
}

/**
 * Fetches the embedded messages for one placement and keeps them current.
 *
 * **A placement returns a list, not a message.** Iterable has no single-message mode, and nothing
 * stops two campaigns running against the same placement at once, so every layer here is plural:
 * the hook holds an array, dismissal removes one entry by id, and the placement stacks whatever
 * survives. There is no "first message wins" anywhere.
 *
 * **It never throws and never rejects.** The explicit AC is that a failed Iterable fetch leaves
 * the dashboard working, so every failure path collapses to "no messages" and the placement
 * simply renders nothing.
 *
 * **Nothing happens until `isIdentified`.** See `useIsIterableIdentified` — a fetch made before
 * the SDK has a user is refused and silently discarded, so the placement would stay empty until
 * the next poll with no error anywhere.
 */
export function useIterableEmbeddedMessages(
  placementId: number,
  isEnabled: boolean,
  isIdentified: boolean,
): UseIterableEmbeddedMessages {
  const [messages, setMessages] = useState<RenderableMessage[]>([])
  const [isPaused, setIsPaused] = useState(() => document.hidden)

  // What this provider has already waved away. Seeded from storage below, because Iterable has no
  // server-side dismissal at all — it keeps serving a dismissed message, so this set is the only
  // thing standing between a provider and an announcement they have already dealt with.
  const dismissedIds = useRef(new Set<string>())

  // Read once per mount rather than per render, so it is a stable dependency below. Safe because
  // the Dev Tools panel forces a reload whenever it changes the pinned fixture.
  const preview = useMemo(getAnnouncementPreview, [])
  const isPreview = preview.length > 0

  // Two passes rather than one compound predicate: `.filter` only narrows the element type when
  // handed a bare type guard, so combining the two conditions would leave these as untyped
  // `IterableEmbeddedMessage`s and push the narrowing problem into the renderer.
  const applyMessages = useCallback((next: IterableEmbeddedMessage[]) => {
    setMessages(next.filter(isRenderable).filter((message) => !dismissedIds.current.has(message.metadata.messageId)))
  }, [])

  // Declared *before* the fetch effect so the ref is filled on the same commit a fetch is started
  // on, rather than a tick later — otherwise the first response paints a banner this provider
  // already dismissed, and it vanishes again on the next poll.
  //
  // Assigning a fresh Set rather than adding to the existing one also clears the previous
  // provider's ids, for the sign-out-and-back-in-as-someone-else case on a shared workstation.
  useEffect(() => {
    if (!isEnabled || isPreview || !isIdentified) return
    const userId = getIterableUserId()
    if (!userId) return
    dismissedIds.current = new Set(getDismissedMessageIds(userId))
  }, [isEnabled, isIdentified, isPreview])

  // The callback and the `onUpdate` subscription below look redundant on a successful fetch — both
  // run — but they cover different cases and neither is removable. `notifyUpdateDelegates` fires
  // only from `trackNewlyRetrieved`, which the manager reaches only when the response carried at
  // least one message; so when a campaign ends and the placement comes back empty, this callback
  // is the *only* thing that clears the banner. The subscription is the only thing that catches an
  // update the SDK initiates on its own.
  const sync = useCallback(async () => {
    try {
      await embeddedManager.syncMessages(APP_PACKAGE_NAME, () => {
        applyMessages(embeddedManager.getMessagesForPlacement(placementId))
      }, [placementId])
    } catch (error) {
      // Defensive only. `retrieveEmbeddedMessages` wraps its whole body in a catch that returns
      // silently for anything but an invalid key or a stopped subscription, so `syncMessages` does
      // not reject on a failed fetch and nothing here reports one to Sentry.
      captureException(error)
      setMessages([])
    }
  }, [applyMessages, placementId])

  useEffect(() => {
    // The flag has to stop the *work*, not just the rendering. Gating only the markup left the
    // kill switch unable to do the one thing it exists for: with the flag off we were still
    // minting a JWT and polling Iterable every ten minutes, so an error-storming integration
    // could not be switched off without a release.
    if (!isEnabled) {
      setMessages([])
      return undefined
    }

    if (isPreview) {
      applyMessages(preview)
      return undefined
    }

    // Identity is the other half of the kill switch's job: until the SDK has a user, every request
    // it makes is refused and thrown away, so fetching here would quietly produce nothing.
    if (!isIdentified) {
      setMessages([])
      return undefined
    }

    const unsubscribe = subscribeToEmbeddedMessages({
      onUpdate: () => applyMessages(embeddedManager.getMessagesForPlacement(placementId)),
      // The SDK calls this on an invalid API key or a disabled subscription. Clearing rather than
      // leaving a stale list up means a revoked key stops showing clinicians old announcements.
      onDisabled: () => setMessages([]),
    })
    void sync()
    return unsubscribe
  }, [applyMessages, isEnabled, isIdentified, isPreview, placementId, preview, sync])

  // Polling stops while the tab is in the background and catches up on return, so a dashboard left
  // open overnight is not making 144 requests nobody will read.
  useEffect(() => {
    const onVisibilityChange = () => {
      const hidden = document.hidden
      setIsPaused(hidden)
      if (!hidden && isEnabled && isIdentified && !isPreview) void sync()
    }
    document.addEventListener('visibilitychange', onVisibilityChange)
    return () => document.removeEventListener('visibilitychange', onVisibilityChange)
  }, [isEnabled, isIdentified, isPreview, sync])

  useInterval(
    () => {
      void sync()
    },
    !isEnabled || !isIdentified || isPreview || isPaused ? null : POLL_INTERVAL_MS,
  )

  const dismiss = useCallback(
    (message: IterableEmbeddedMessage, buttonIdentifier: string) => {
      const { messageId } = message.metadata
      dismissedIds.current.add(messageId)
      setMessages((current) => current.filter((item) => item.metadata.messageId !== messageId))

      // The preview check *is* needed here, unlike for `trackDismiss` below: this module is ours,
      // so nothing else suppresses it, and persisting a fixture id would hide a real announcement
      // that later happened to carry the same invented id.
      const userId = getIterableUserId()
      if (!isPreview && userId) {
        recordDismissedMessageId(userId, messageId, message.metadata.campaignId)
        // After the local write, so the field carries the dismissal that just happened.
        publishDismissedCampaigns(getDismissedCampaignIds(userId))
      }

      // No preview check here: `trackDismiss` is one of the reporting calls `lib/iterable/embedded`
      // suppresses while a fixture is pinned, so there is one switch rather than one per call site.
      trackDismiss(message, buttonIdentifier)
    },
    [isPreview],
  )

  return { messages, dismiss }
}
